import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@cutilei/ui'
import { useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Phone from 'services/Phone'
import Address from 'services/Address'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import AlertDialog from 'components/AlertDialog'
import OptionDialog from 'components/OptionDialog'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { isValid as isValidCNPJ, format as formatCNPJ } from '@fnando/cnpj'
import { format as formatCPF } from '@fnando/cpf'
import { FaStar } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Container,
  Image,
  Row,
  RatingContainer,
  SubTitle,
  InfoText,
  LinkText,
  Block
} from './styles'

export default function BusinessDetailsModal ({
  visible,
  data: business,
  onUpdate: handleUpdate = () => null,
  onClose: handleClose = () => null
}) {
  const navigate = useNavigate ()
  const [loading, setLoading] = useState (true)
  const [submitting, setSubmitting] = useState (false)
  const [workingDays, setWorkingDays] = useState ([])
  const [isBusinessActive, setBusinessActive] = useState (business?.status === 'active' ?? false)
  const [showActivationDialog, setShowActivationDialog] = useState (false)
  const [showArchiveDialog, setShowArchiveDialog] = useState (false)
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState (false)
  const token = AuthService.getToken ()

  const statusColor = useMemo (() => {
    switch (business?.status) {
      case 'active': return '#44CF6C'
      case 'pending':
      case 'archived': return '#FFC100'
      default: return '#FF3939'
    }
  }, [business?.status])

  const statusText = useMemo (() => {
    switch (business?.status) {
      case 'active': return 'Ativo'
      case 'pending': return 'Pendente'
      case 'inactive': return 'Desativado pelo admin'
      case 'deleted': return 'Excluído pelo usuário'
      case 'archived': return 'Arquivado'
      default: return 'Desconhecido'
    }
  }, [business?.status])

  useEffect (() => {
    if (!business) return
    getData ()
    setBusinessActive (business.status === 'active')
  }, [business])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const getData = async () => {
    try {
      const { data: workingDays } = await cutileiApi.get (
        `/businesses/${business.id}/working_days`, requestConfig
      )

      setWorkingDays (workingDays)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  if (!business) return null
  
  const address = Address.formatAddress (business?.addresses[0])
  const mapsAddress = Address.formatAddressWithNoComplement (business?.addresses[0])
  const addressUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent (mapsAddress)}`

  const handleAccessBusiness = () => {
    AuthService.accessBusiness ({
      id: business.id,
      name: business.name,
      email: business.email,
      logo: business.logo,
      access_level: 'business'
    })
    
    navigate ('/business', { replace: true })
  }

  const changeBusinessStatus = async status => {
    try {
      setSubmitting (true)

      await cutileiApi.put (
        `/businesses/${business.id}/status`, { status }, requestConfig
      )

      setBusinessActive (status === 'active')
      handleUpdate ({ ...business, status })
    } catch (error) {
      logApiError (error)
    } finally {
      setSubmitting (false)
    }
  }

  const toggleActivationDialog = () => setShowActivationDialog (!showActivationDialog)
  const toggleArchiveDialog = () => setShowArchiveDialog (!showArchiveDialog)
  const toggleUnarchiveDialog = () => setShowUnarchiveDialog (!showUnarchiveDialog)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{business.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {loading ? <Loading/> : (
            <Container>
              <Row style={{position: 'relative', width: 'auto'}}>
                <Image src={business.logo}/>
                <RatingContainer>
                  <FaStar size={16} color='#FFC100'/>
                  <SubTitle>
                    {business?.average_rating}
                  </SubTitle>
                </RatingContainer>
              </Row>
              <Block>
                <Row>
                  <SubTitle>Razão social: &nbsp;</SubTitle>
                  <InfoText>{business.corporate_name}</InfoText>
                </Row>
                <Row>
                  <SubTitle>Responsável: &nbsp;</SubTitle>
                  <InfoText>{business.owner}</InfoText>
                </Row>
                <Row>
                  <SubTitle>
                    {isValidCNPJ (business.cnpj) ? 'CNPJ' : 'CPF'}: &nbsp;
                  </SubTitle>
                  <InfoText>
                    {isValidCNPJ (business.cnpj) ? formatCNPJ (business.cnpj) : formatCPF (business.cnpj)}
                  </InfoText>
                </Row>
                <Row>
                  <SubTitle>Contato: &nbsp;</SubTitle>
                  <LinkText
                    href={`https://api.whatsapp.com/send?phone=${business.cellphone.replace (/\D/g, '')}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{width: 'auto', color: '#44CF6C'}}
                  >
                    <SubTitle>{Phone.formatPhone (business.cellphone, true)}</SubTitle>
                  </LinkText>
                </Row>
                <Row>
                  <SubTitle>Status: &nbsp;</SubTitle>
                  <SubTitle style={{color: statusColor}}>
                    {statusText}
                  </SubTitle>
                </Row>
              </Block>
              <Block>
                <SubTitle>Endereço:</SubTitle>
                <LinkText href={addressUrl} target='_blank' rel='noopener noreferrer'>
                  <SubTitle>{address}</SubTitle>
                </LinkText>
              </Block>
              <Block>
                <SubTitle>Dias disponíveis:</SubTitle>
                {workingDays.length > 0 ? workingDays.map (day => (
                  <Row key={day.id}>
                    <InfoText>
                      {day.name}: &nbsp;
                    </InfoText>
                    <SubTitle>
                      {`${DateTime.fromISO (day.opening).toFormat ('H:mm')} - ${DateTime.fromISO (day.closing).toFormat ('H:mm')}`}
                    </SubTitle>
                  </Row>
                )) : (
                  <InfoText>Este salão ainda não cadastrou os dias disponíveis</InfoText>
                )}
              </Block>
              <Block>
                <Row>
                  <SubTitle>Cadastro: &nbsp;</SubTitle>
                  <InfoText>
                    {DateTime.fromISO (business.created_at).toFormat ('dd/MM/yyyy - H:mm:ss')}
                  </InfoText>
                </Row>
                <Row>
                  <SubTitle>Última alteração: &nbsp;</SubTitle>
                  <InfoText>
                    {DateTime.fromISO (business.updated_at).toFormat ('dd/MM/yyyy - H:mm:ss')}
                  </InfoText>
                </Row>
                <Row>
                  <SubTitle>ID: &nbsp;</SubTitle>
                  <InfoText>{business.id}</InfoText>
                </Row>
              </Block>
              <Row style={{marginTop: 5, gap: 10}}>
                <SecondaryButton
                  label='Acessar salão'
                  onClick={handleAccessBusiness}
                  width={140}
                />
                {business.status !== 'archived' && (
                  <MainButton
                    label={isBusinessActive ? 'Desativar salão' : 'Ativar salão'}
                    onClick={toggleActivationDialog}
                    dangerous={isBusinessActive}
                    disabled={submitting}
                    loading={submitting}
                    width={150}
                  />
                )}
                {['pending', 'inactive'].includes (business.status) && (
                  <SecondaryButton
                    label='Arquivar salão'
                    onClick={toggleArchiveDialog}
                    disabled={submitting}
                    loading={submitting}
                    width={150}
                  />
                )}
                {business.status === 'archived' && (
                  <MainButton
                    label='Desarquivar salão'
                    onClick={toggleUnarchiveDialog}
                    disabled={submitting}
                    loading={submitting}
                    width={150}
                  />
                )}
              </Row>
              <AlertDialog
                visible={showActivationDialog}
                title='Atenção!'
                message={`Deseja realmente ${isBusinessActive ? 'desativar' : 'ativar'} este salão?`}
                confirmText='Sim'
                cancelText='Não'
                onConfirm={() => changeBusinessStatus (isBusinessActive ? 'inactive' : 'active')}
                onClose={toggleActivationDialog}
                dangerous={isBusinessActive}
              />
              <AlertDialog
                visible={showArchiveDialog}
                title='Atenção!'
                message={`Deseja realmente arquivar este salão?`}
                confirmText='Sim'
                cancelText='Não'
                onConfirm={() => changeBusinessStatus ('archived')}
                onClose={toggleArchiveDialog}
                dangerous={true}
              />
              <OptionDialog
                visible={showUnarchiveDialog}
                title='Atenção!'
                message='Selecione o status que deseja atribuir ao salão ao desarquivá-lo:'
                options={[
                  {label: 'Ativo', onClick: () => changeBusinessStatus ('active'), dangerous: false},
                  {label: 'Pendente', onClick: () => changeBusinessStatus ('pending'), dangerous: false},
                  {label: 'Inativo', onClick: () => changeBusinessStatus ('inactive'), dangerous: true}
                ]}
                onClose={toggleUnarchiveDialog}
              />
            </Container>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
